import React, { useState, useEffect } from 'react';
import { PlusIcon, MagnifyingGlassIcon, ChevronDownIcon, XMarkIcon, ChevronUpIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Squares2X2Icon, ArrowPathIcon } from '@heroicons/react/24/solid';
import * as XLSX from 'xlsx';
import SongsTable from '../components/SongsTable';
import NewSongModal from '../components/NewSongModal';
import EditSongModal from '../components/EditSongModal';
import ChartViewModal from '../components/ChartViewModal';
import AddToSetlistModal from '../components/AddToSetlistModal';
import { getSongs, createSong, updateSong, deleteSong } from '../services/api';

const SongsPage = () => {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilters, setStatusFilters] = useState(new Set()); // Set of selected statuses
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [progressFilter, setProgressFilter] = useState(false);
  const [randomSongId, setRandomSongId] = useState(null);
  const [rollHistory, setRollHistory] = useState(new Map()); // Map<songId, timestamp>
  const [rollMessage, setRollMessage] = useState('');
  const [showNewSongModal, setShowNewSongModal] = useState(false);
  const [editingSong, setEditingSong] = useState(null);
  const [viewingChart, setViewingChart] = useState(null);
  const [addingToSetlist, setAddingToSetlist] = useState(null);
  const [sortField, setSortField] = useState('title'); // Default sort by title
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [isShuffled, setIsShuffled] = useState(false);

  useEffect(() => {
    loadSongs();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isStatusDropdownOpen && !event.target.closest('.status-dropdown')) {
        setIsStatusDropdownOpen(false);
      }
      if (isSortDropdownOpen && !event.target.closest('.sort-dropdown')) {
        setIsSortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isStatusDropdownOpen, isSortDropdownOpen]);

  const loadSongs = async () => {
    try {
      setLoading(true);
      const data = await getSongs();
      setSongs(data);
    } catch (error) {
      console.error('Error loading songs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSong = async (songData) => {
    try {
      const newSong = await createSong(songData);
      setSongs([...songs, newSong]);
      setShowNewSongModal(false);
    } catch (error) {
      console.error('Error creating song:', error);
    }
  };

  const handleUpdateSong = async (songData) => {
    try {
      const updatedSong = await updateSong(editingSong.id, songData);
      setSongs(songs.map(song => 
        song.id === editingSong.id ? updatedSong : song
      ));
      setEditingSong(null);
    } catch (error) {
      console.error('Error updating song:', error);
    }
  };

  const handleDeleteSong = async (songId) => {
    if (window.confirm('Are you sure you want to delete this song?')) {
      try {
        await deleteSong(songId);
        setSongs(songs.filter(song => song.id !== songId));
      } catch (error) {
        console.error('Error deleting song:', error);
      }
    }
  };

  const handleViewChart = (song) => {
    setViewingChart(song);
  };

  const handleChartSave = async (chart) => {
    try {
      const updatedSong = await updateSong(viewingChart.id, { chart });
      setSongs(songs.map(song =>
        song.id === viewingChart.id ? updatedSong : song
      ));
      setViewingChart({ ...viewingChart, chart });
    } catch (error) {
      console.error('Error updating chart:', error);
    }
  };

  const handleChartLayoutSave = async (chartLayout) => {
    try {
      const updatedSong = await updateSong(viewingChart.id, { chart_layout: chartLayout });
      setSongs(songs.map(song =>
        song.id === viewingChart.id ? updatedSong : song
      ));
      setViewingChart({ ...viewingChart, chart_layout: chartLayout });
    } catch (error) {
      console.error('Error updating chart layout:', error);
    }
  };

  const handleChartConvertToV2 = async (v2Chart) => {
    try {
      // Save the V2 chart and clear the chart_layout (layout is now embedded in V2)
      const updatedSong = await updateSong(viewingChart.id, { chart: v2Chart, chart_layout: null });
      setSongs(songs.map(song =>
        song.id === viewingChart.id ? updatedSong : song
      ));
      setViewingChart({ ...viewingChart, chart: v2Chart, chart_layout: null });
    } catch (error) {
      console.error('Error converting chart to V2:', error);
    }
  };

  const handleChartDelete = async () => {
    try {
      const updatedSong = await updateSong(viewingChart.id, { chart: null });
      setSongs(songs.map(song => 
        song.id === viewingChart.id ? updatedSong : song
      ));
    } catch (error) {
      console.error('Error deleting chart:', error);
    }
  };

  const handleRollSong = () => {
    if (songs.length === 0) return;
    
    // Get current time
    const now = Date.now();
    const threeHoursMs = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
    
    // Filter songs that haven't been rolled in the last 3 hours and are not BACKLOG
    const eligibleSongs = songs.filter(song => {
      // Exclude BACKLOG songs from rolling
      if (song.status === 'BACKLOG') {
        return false;
      }
      const lastRolled = rollHistory.get(song.id);
      return !lastRolled || (now - lastRolled) > threeHoursMs;
    });
    
    if (eligibleSongs.length === 0) {
      setRollMessage('All eligible songs have been rolled');
      setTimeout(() => setRollMessage(''), 3000); // Clear message after 3 seconds
      return;
    }
    
    // Pick random song from eligible songs
    const randomIndex = Math.floor(Math.random() * eligibleSongs.length);
    const randomSong = eligibleSongs[randomIndex];
    
    // Update roll history
    const newRollHistory = new Map(rollHistory);
    newRollHistory.set(randomSong.id, now);
    setRollHistory(newRollHistory);
    
    setRandomSongId(randomSong.id);
    setRollMessage('');
    
    // Clear other filters when rolling
    setSearchTerm('');
    setStatusFilters(new Set());
    setProgressFilter(false);
  };

  const handleShuffleSongs = () => {
    if (songs.length === 0) return;
    
    // Create a shuffled copy of the songs array using Fisher-Yates algorithm
    const shuffledSongs = [...songs];
    for (let i = shuffledSongs.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledSongs[i], shuffledSongs[j]] = [shuffledSongs[j], shuffledSongs[i]];
    }
    
    setSongs(shuffledSongs);
    setIsShuffled(true);
    
    // Only clear random song selection and close dropdowns, preserve filters
    setRandomSongId(null);
    setRollMessage('');
    setIsStatusDropdownOpen(false);
    setIsSortDropdownOpen(false);
  };

  const handleClearAllFilters = () => {
    setSearchTerm('');
    setStatusFilters(new Set());
    setProgressFilter(false);
    setRandomSongId(null);
    setRollMessage('');
    setIsStatusDropdownOpen(false);
    setIsSortDropdownOpen(false);
    setSortField('title');
    setSortDirection('asc');
    setIsShuffled(false);
  };

  const handleStatusFilterChange = (status) => {
    const newStatusFilters = new Set(statusFilters);
    if (newStatusFilters.has(status)) {
      newStatusFilters.delete(status);
    } else {
      newStatusFilters.add(status);
    }
    setStatusFilters(newStatusFilters);
  };

  const handleSortChange = (field) => {
    if (sortField === field) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a different field, set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
    setIsSortDropdownOpen(false);
    setIsShuffled(false); // Clear shuffle state when sorting
  };

  const filteredAndSortedSongs = songs.filter(song => {
    // If a random song is selected, only show that song
    if (randomSongId) {
      return song.id === randomSongId;
    }
    
    // Apply status filters
    if (statusFilters.size > 0 && !statusFilters.has(song.status)) {
      return false;
    }
    
    if (searchTerm && !song.title.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !song.artist.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  }).sort((a, b) => {
    // Skip sorting if songs are shuffled
    if (isShuffled) {
      return 0;
    }
    
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    // Handle string fields
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    // Handle sorting direction
    if (sortDirection === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const handleDownload = () => {
    const rows = filteredAndSortedSongs.map(song => ({
      Title: song.title,
      Artist: song.artist,
      Status: song.status === 'IN_PROGRESS' ? 'In Progress' :
              song.status === 'TODO' ? 'To Do' :
              song.status === 'BACKLOG' ? 'Backlog' : 'Good',
      Duration: song.duration ? `${Math.floor(song.duration / 60)}:${String(song.duration % 60).padStart(2, '0')}` : '',
      BPM: song.bpm || '',
      Key: song.key || '',
      Year: song.year || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Songs');
    XLSX.writeFile(wb, 'songs.xlsx');
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Songs</h1>
          <button
            onClick={handleDownload}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center space-x-2"
            title="Download filtered songs as XLSX"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
            <span>Download</span>
          </button>
        </div>
        
        {/* Action Bar */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            {/* Quick Filters */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Quick Filters:</span>
              <button
                onClick={handleClearAllFilters}
                className={`px-3 py-1 rounded text-sm ${
                  statusFilters.size === 0 && !progressFilter && !randomSongId && !searchTerm
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-700 border border-gray-300'
                }`}
              >
                All
              </button>
              
              <button
                onClick={() => {
                  setStatusFilters(new Set(['GOOD', 'IN_PROGRESS']));
                  setProgressFilter(false);
                  setRandomSongId(null);
                }}
                className={`px-3 py-1 rounded text-sm ${
                  statusFilters.size === 2 && statusFilters.has('GOOD') && statusFilters.has('IN_PROGRESS')
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-700 border border-gray-300'
                }`}
              >
                Ready
              </button>
              
              <button
                onClick={() => {
                  setStatusFilters(new Set(['IN_PROGRESS', 'TODO']));
                  setProgressFilter(false);
                  setRandomSongId(null);
                }}
                className={`px-3 py-1 rounded text-sm ${
                  statusFilters.size === 2 && statusFilters.has('IN_PROGRESS') && statusFilters.has('TODO')
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-700 border border-gray-300'
                }`}
              >
                Working
              </button>
              
              <button
                onClick={() => {
                  if (searchTerm.toLowerCase() === 'doulos') {
                    // If Doulos filter is active, turn it off
                    setSearchTerm('');
                  } else {
                    // If Doulos filter is not active, turn it on
                    setSearchTerm('Doulos');
                  }
                  setStatusFilters(new Set());
                  setProgressFilter(false);
                  setRandomSongId(null);
                }}
                className={`px-3 py-1 rounded text-sm ${
                  searchTerm.toLowerCase() === 'doulos'
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-700 border border-gray-300'
                }`}
              >
                Doulos
              </button>
              
              {/* Status Dropdown */}
              <div className="relative status-dropdown">
                <button
                  onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                  className={`px-3 py-1 rounded text-sm flex items-center space-x-1 ${
                    statusFilters.size > 0
                      ? 'bg-blue-100 text-blue-700 border border-blue-300'
                      : 'bg-gray-100 text-gray-700 border border-gray-300'
                  }`}
                >
                  <span>Status {statusFilters.size > 0 && `(${statusFilters.size})`}</span>
                  <ChevronDownIcon className="w-3 h-3" />
                </button>
                
                {isStatusDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 min-w-40">
                    <div className="p-2 space-y-2">
                      {['GOOD', 'IN_PROGRESS', 'TODO', 'BACKLOG'].map((status) => (
                        <label key={status} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={statusFilters.has(status)}
                            onChange={() => handleStatusFilterChange(status)}
                            className="rounded text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm">
                            {status === 'IN_PROGRESS' ? 'In Progress' : 
                             status === 'TODO' ? 'To Do' : 
                             status === 'BACKLOG' ? 'Backlog' : 'Good'}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Sort Dropdown */}
              <div className="relative sort-dropdown">
                <button
                  onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                  className="px-3 py-1 rounded text-sm flex items-center space-x-1 bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200"
                >
                  <span>Sort: {sortField === 'title' ? 'Title' : sortField === 'artist' ? 'Artist' : sortField === 'status' ? 'Status' : 'Created'}</span>
                  {sortDirection === 'asc' ? 
                    <ChevronUpIcon className="w-3 h-3" /> : 
                    <ChevronDownIcon className="w-3 h-3" />
                  }
                </button>
                
                {isSortDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 min-w-40">
                    <div className="p-2 space-y-1">
                      {[
                        { field: 'title', label: 'Title' },
                        { field: 'artist', label: 'Artist' },
                        { field: 'status', label: 'Status' },
                        { field: 'created_at', label: 'Created' }
                      ].map(({ field, label }) => (
                        <button
                          key={field}
                          onClick={() => handleSortChange(field)}
                          className={`w-full text-left px-2 py-1 text-sm rounded hover:bg-gray-50 flex items-center justify-between ${
                            sortField === field ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                          }`}
                        >
                          <span>{label}</span>
                          {sortField === field && (
                            sortDirection === 'asc' ? 
                              <ChevronUpIcon className="w-3 h-3" /> : 
                              <ChevronDownIcon className="w-3 h-3" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Roll Button */}
            <button
              onClick={handleRollSong}
              className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 flex items-center space-x-2"
              title="Roll for random song"
            >
              <Squares2X2Icon className="w-5 h-5" />
              <span>Roll</span>
            </button>
            
            {/* Shuffle Button */}
            <button
              onClick={handleShuffleSongs}
              className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 flex items-center space-x-2"
              title="Shuffle song list"
            >
              <ArrowPathIcon className="w-5 h-5" />
              <span>Shuffle</span>
            </button>
            
            {/* Search Bar */}
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search songs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* New Song Button */}
            <button
              onClick={() => setShowNewSongModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
            >
              <PlusIcon className="w-5 h-5" />
              <span>New Song</span>
            </button>
          </div>
        </div>

        {/* Roll Message */}
        {rollMessage && (
          <div className="mb-4 p-3 bg-yellow-100 border border-yellow-300 rounded-md text-yellow-800 text-center">
            {rollMessage}
          </div>
        )}

        {/* Songs Table */}
        {loading ? (
          <div className="text-center py-8">Loading songs...</div>
        ) : (
          <SongsTable
            songs={filteredAndSortedSongs}
            onEdit={setEditingSong}
            onDelete={handleDeleteSong}
            onViewChart={handleViewChart}
            onAddToSetlist={setAddingToSetlist}
          />
        )}
      </div>

      {/* Modals */}
      {showNewSongModal && (
        <NewSongModal
          onClose={() => setShowNewSongModal(false)}
          onSave={handleCreateSong}
        />
      )}

      {editingSong && (
        <EditSongModal
          song={editingSong}
          onClose={() => setEditingSong(null)}
          onSave={handleUpdateSong}
        />
      )}

      {viewingChart && (
        <ChartViewModal
          isOpen={true}
          onClose={() => setViewingChart(null)}
          chart={viewingChart.chart}
          chartLayout={viewingChart.chart_layout}
          songTitle={viewingChart.title}
          onSave={handleChartSave}
          onDelete={handleChartDelete}
          onSaveLayout={handleChartLayoutSave}
          onConvertToV2={handleChartConvertToV2}
        />
      )}

      {addingToSetlist && (
        <AddToSetlistModal
          song={addingToSetlist}
          songs={songs}
          onClose={() => setAddingToSetlist(null)}
        />
      )}
    </div>
  );
};

export default SongsPage;